import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    FaArchive, FaCheckCircle, FaExternalLinkAlt, FaFileAlt, FaHeart, FaLink, FaPen, FaPlus,
    FaRegStar, FaSearch, FaShoppingBag, FaSlidersH, FaStar, FaTag, FaEyeSlash,
} from "react-icons/fa";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { Badge, EmptyState } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { onWishlistMoney, onWishlistPriorityLabel } from "../_UTILS/wishlist.util";
import { copyWishlistExternalLink } from "../_UTILS/wishlistShare.util";

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("es-AR", { dateStyle: "medium", timeStyle: "short" });
};

const matches = (item, query) => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return true;
    return [item?.name, item?.description, item?.store, item?.url, item?.notes, item?.priority, item?.currency]
        .some((value) => String(value || "").toLowerCase().includes(q));
};

export const WishlistCategoryWorkspaceModal = ({
    isOpen,
    category = null,
    items = [],
    focusItemId = null,
    onFocusItemHandled,
    onClose,
    onNewItem,
    onEditItem,
    onEditCategory,
    onExportItem,
    onToggleFavorite,
    onTogglePurchased,
    onArchiveItem,
    isLoading = false,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const emptyFilters = () => ({
        purchase: "ALL", favorite: "ALL", priority: "ALL", store: "",
        dateField: "UPDATED", dateRange: [null, null], dateExact: null,
    });
    const [filters, setFilters] = useState(() => emptyFilters());
    const advancedCount = [
        filters.purchase !== "ALL", filters.favorite !== "ALL", filters.priority !== "ALL", String(filters.store || "").trim(),
        filters.dateRange?.[0] || filters.dateRange?.[1], filters.dateExact,
    ].filter(Boolean).length;

    const rows = useMemo(() => (Array.isArray(items) ? items : [])
        .filter((item) => String(item.categoryId || "") === String(category?.id || ""))
        .sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite))
            || (Number(!!a.isPurchased) - Number(!!b.isPurchased))
            || String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""))),
    [items, category?.id]);

    const visible = useMemo(() => rows.filter((item) => {
        if (!matches(item, search)) return false;
        if (filters.purchase === "PURCHASED" && !item.isPurchased) return false;
        if (filters.purchase === "PENDING" && item.isPurchased) return false;
        if (filters.favorite === "YES" && !item.isFavorite) return false;
        if (filters.favorite === "NO" && item.isFavorite) return false;
        if (filters.priority !== "ALL" && String(item.priority || "") !== filters.priority) return false;
        if (String(filters.store || "").trim() && !String(item.store || "").toLowerCase().includes(String(filters.store).trim().toLowerCase())) return false;

        const rawDate = filters.dateField === "CREATED" ? item.createdAt : item.updatedAt;
        return onDateMatches(rawDate, filters.dateRange, filters.dateExact);
    }), [rows, search, filters]);
    const selected = visible.find((item) => String(item.id) === String(selectedId)) || null;

    useEffect(() => {
        if (!isOpen) return;
        setSearch("");
        setAdvancedOpen(searchParams.get("advanced") === "1");
        setFilters(emptyFilters());
        // La categoría abre sin selección salvo que la URL tenga un artículo explícito.
        setSelectedId(searchParams.get("item") || null);
    }, [isOpen, category?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isOpen || isLoading) return;
        const routed = searchParams.get("item");
        if (!routed) return;
        if (rows.some((item) => String(item.id) === String(routed))) {
            setSelectedId(String(routed));
        }
        // No borres el id de la URL mientras hidratan los datos.
        // Si la API tarda un render, el registro sigue siendo recuperable al recargar.
    }, [isOpen, isLoading, searchParams, rows, setSearchParams]);

    useEffect(() => {
        if (!isOpen || !focusItemId) return;
        if (!rows.some((item) => String(item.id) === String(focusItemId))) return;
        setSelectedId(focusItemId);
        onFocusItemHandled?.(focusItemId);
    }, [focusItemId, rows, isOpen, onFocusItemHandled]);

    if (!category) return null;

    const updateRouteState = (patch = {}) => {
        const next = new URLSearchParams(window.location.search);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") next.delete(key);
            else next.set(key, String(value));
        });
        setSearchParams(next, { replace: true });
    };

    const selectItem = (id) => {
        setSelectedId(id);
        updateRouteState({ item: id, preview: null });
    };

    const toggleAdvanced = () => {
        const next = !advancedOpen;
        setAdvancedOpen(next);
        updateRouteState({ advanced: next ? "1" : null });
    };

    const hidePreview = () => {
        setSelectedId(null);
        updateRouteState({ item: null, preview: null });
    };

    const selectedTotal = selected?.price === null || selected?.price === undefined
        ? null
        : Number(selected.price) * Math.max(1, Number(selected.quantity || 1));

    return <AppModal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        className="LStudio__wishlist_workspace_modal"
        title={category.name || "Wish List"}
        description={category.description || null}
    >
        <div className="LStudio__wishlist_workspace">
            <aside className="LStudio__wishlist_workspace_sidebar">
                <div className="LStudio__wishlist_workspace_actions">
                    <AppButton size="sm" icon={<FaPlus />} onClick={() => onNewItem?.(category)}>Nuevo artículo</AppButton>
                    <AppButton size="sm" variant="outline" tone="neutral" icon={<FaPen />} onClick={() => onEditCategory?.(category)}>Editar categoría</AppButton>
                </div>

                <div className="LStudio__wishlist_workspace_filterbar">
                    <label className="LStudio__wishlist_workspace_search">
                        <FaSearch />
                        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar artículos..." />
                    </label>
                    <button type="button" className="LStudio__wishlist_workspace_filter_toggle" data-open={advancedOpen ? "true" : "false"}
                        onClick={toggleAdvanced} aria-label="Filtros avanzados" title="Filtros avanzados">
                        <FaSlidersH />
                    </button>
                </div>

                <AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} className="LStudio__wishlist_workspace_advanced" onClear={() => setFilters(emptyFilters())}>
                    <FieldSelect id="wishlist_ws_purchase" label="Compra" value={filters.purchase}
                        onChange={(event) => setFilters((current) => ({ ...current, purchase: event.target.value }))}
                        options={[{ value: "ALL", label: "Todos" }, { value: "PENDING", label: "Pendientes" }, { value: "PURCHASED", label: "Comprados" }]} />
                    <FieldSelect id="wishlist_ws_favorite" label="Destacado" value={filters.favorite}
                        onChange={(event) => setFilters((current) => ({ ...current, favorite: event.target.value }))}
                        options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Sí" }, { value: "NO", label: "No" }]} />
                    <FieldSelect id="wishlist_ws_priority" label="Prioridad" value={filters.priority}
                        onChange={(event) => setFilters((current) => ({ ...current, priority: event.target.value }))}
                        options={[{ value: "ALL", label: "Todas" }, { value: "LOW", label: "Baja" }, { value: "MEDIUM", label: "Media" }, { value: "HIGH", label: "Alta" }, { value: "URGENT", label: "Urgente" }]} />
                    <FieldInput id="wishlist_ws_store" label="Tienda" value={filters.store}
                        onChange={(event) => setFilters((current) => ({ ...current, store: event.target.value }))} placeholder="Tienda" />
                    <FieldSelect id="wishlist_ws_date" label="Fecha" value={filters.dateField}
                        onChange={(event) => setFilters((current) => ({ ...current, dateField: event.target.value }))}
                        options={[{ value: "UPDATED", label: "Última actualización" }, { value: "CREATED", label: "Creación" }]} />
                    <FieldDate id="wishlist_ws_from" label="Desde" value={filters.dateRange?.[0] || null}
                        onChange={(date) => setFilters((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                    <FieldDate id="wishlist_ws_to" label="Hasta" value={filters.dateRange?.[1] || null}
                        onChange={(date) => setFilters((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                    <FieldDate id="wishlist_ws_exact" label="Fecha exacta" value={filters.dateExact}
                        onChange={(date) => setFilters((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                </AdvancedFilterPanel>

                <div className="LStudio__wishlist_workspace_count">{visible.length} {visible.length === 1 ? "artículo" : "artículos"}</div>

                <div className="LStudio__wishlist_workspace_list">
                    {visible.map((item) => (
                        <button key={item.id} type="button" data-active={String(item.id) === String(selectedId)} onClick={() => selectItem(item.id)}>
                            <span className="LStudio__wishlist_workspace_thumb">
                                {item.imageUrl ? <img src={item.imageUrl} alt="" loading="lazy" /> : <FaHeart />}
                            </span>
                            <span className="LStudio__wishlist_workspace_item_body">
                                <span className="LStudio__wishlist_workspace_item_title">
                                    <strong>{item.name || "Sin nombre"}</strong>
                                    <span className="LStudio__wishlist_workspace_item_icons">
                                        {item.isFavorite && <FaStar title="Destacado" />}
                                        {item.isPurchased && <FaCheckCircle title="Comprado" />}
                                    </span>
                                </span>
                                <small>{[item.store, onWishlistPriorityLabel(item.priority)].filter(Boolean).join(" · ")}</small>
                                <span>{item.price === null || item.price === undefined ? "Sin precio" : onWishlistMoney(item.price, item.currency)}</span>
                            </span>
                        </button>
                    ))}
                    {!visible.length && <div className="LStudio__wishlist_workspace_empty_list">
                        {rows.length ? "No hay coincidencias." : "Esta categoría todavía no tiene artículos."}
                    </div>}
                </div>
            </aside>

            {selected ? <section className="LStudio__wishlist_workspace_reader">
                <div className="LStudio__wishlist_workspace_toolbar">
                    <div className="LStudio__wishlist_workspace_badges">
                        <Badge tone="neutral" size="sm">{onWishlistPriorityLabel(selected.priority)}</Badge>
                        {selected.isPurchased && <Badge tone="ok" size="sm">Comprado</Badge>}
                        {selected.isFavorite && <Badge tone="accent" size="sm">Destacado</Badge>}
                    </div>
                    <div className="LStudio__wishlist_workspace_toolbar_actions">
                        <AppButton size="sm" variant="ghost" tone="neutral"
                            icon={selected.isFavorite ? <FaStar /> : <FaRegStar />}
                            onClick={() => onToggleFavorite?.(selected)}>
                            {selected.isFavorite ? "Quitar destacado" : "Destacar"}
                        </AppButton>
                        <AppButton size="sm" variant={selected.isPurchased ? "soft" : "outline"}
                            tone={selected.isPurchased ? "ok" : "neutral"} icon={<FaCheckCircle />}
                            onClick={() => onTogglePurchased?.(selected)}>
                            {selected.isPurchased ? "Comprado" : "Marcar comprado"}
                        </AppButton>
                        {selected.url && <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaLink />} onClick={() => copyWishlistExternalLink(selected.url)}>Copiar link</AppButton>}
                        {onExportItem && <AppButton size="sm" variant="outline" tone="neutral" icon={<FaFileAlt />} onClick={() => onExportItem(selected, category)}>Guardar</AppButton>}
                        <AppButton size="sm" variant="outline" tone="neutral" icon={<FaPen />} onClick={() => onEditItem?.(selected, category)}>Editar</AppButton>
                        <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaEyeSlash />} onClick={hidePreview}>Ocultar</AppButton>
                        <AppButton size="sm" variant="outline" tone="danger" icon={<FaArchive />} onClick={() => onArchiveItem?.(selected)}>Archivar</AppButton>
                    </div>
                </div>

                <div className="LStudio__wishlist_workspace_reader_scroll">
                    <article className="LStudio__wishlist_workspace_document">
                        <header className="LStudio__wishlist_workspace_document_head" data-has-image={selected.imageUrl ? "true" : "false"}>
                            {selected.imageUrl && <div className="LStudio__wishlist_workspace_hero_image"><img src={selected.imageUrl} alt="" /></div>}
                            <div className="LStudio__wishlist_workspace_document_identity">
                                <small>{category.name}</small>
                                <h1>{selected.name || "Sin nombre"}</h1>
                                {selected.description && <p>{selected.description}</p>}
                            </div>
                        </header>

                        <section className="LStudio__wishlist_workspace_data_grid">
                            <div><span>Precio</span><strong>{selected.price === null || selected.price === undefined ? "—" : onWishlistMoney(selected.price, selected.currency)}</strong></div>
                            <div><span>Cantidad</span><strong>{selected.quantity || 1}</strong></div>
                            <div><span>Total</span><strong>{selectedTotal === null ? "—" : onWishlistMoney(selectedTotal, selected.currency)}</strong></div>
                            <div><span>Tienda</span><strong>{selected.store || "—"}</strong></div>
                        </section>

                        {selected.notes && <section className="LStudio__wishlist_workspace_notes">
                            <strong>Notas</strong>
                            <p>{selected.notes}</p>
                        </section>}

                        {selected.url && <AppButton variant="outline" tone="neutral" href={selected.url} target="_blank" rel="noreferrer" icon={<FaExternalLinkAlt />}>Abrir artículo</AppButton>}
                    </article>

                    <aside className="LStudio__wishlist_workspace_meta">
                        <section>
                            <strong>Estado</strong>
                            <div><span>Prioridad</span><b>{onWishlistPriorityLabel(selected.priority)}</b></div>
                            <div><span>Compra</span><b>{selected.isPurchased ? "Comprado" : "Pendiente"}</b></div>
                            <div><span>Destacado</span><b>{selected.isFavorite ? "Sí" : "No"}</b></div>
                        </section>
                        <section>
                            <strong>Origen</strong>
                            <div><span><FaTag /> Tienda</span><b>{selected.store || "—"}</b></div>
                            <div><span><FaShoppingBag /> Categoría</span><b>{category.name}</b></div>
                        </section>
                        <section>
                            <strong>Registro</strong>
                            <div><span>Creado</span><b>{formatDate(selected.createdAt)}</b></div>
                            <div><span>Actualizado</span><b>{formatDate(selected.updatedAt)}</b></div>
                        </section>
                    </aside>
                </div>
            </section> : <main className="LStudio__wishlist_workspace_preview">
                <EmptyState icon={<FaHeart />}
                    title={rows.length ? "Elegí un artículo" : "Esta categoría está vacía"}
                    description={rows.length ? null : null}
                    action={!rows.length ? <AppButton icon={<FaPlus />} onClick={() => onNewItem?.(category)}>Nuevo artículo</AppButton> : null} />
            </main>}
        </div>
    </AppModal>;
};
